import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Header, API, Footer } from "../App";
import { Button } from "@/components/ui/button";
import { Clock, Shield, MapPin, Calendar, ArrowRight } from "lucide-react";
import axios from "axios";

const SharedOfferScreen = () => {
  const { lockedOfferId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        const res = await axios.get(`${API}/offers/share/${lockedOfferId}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? "Offer not found or has expired." : "Unable to load offer.");
      } finally {
        setLoading(false);
      }
    };
    fetchOffer();
  }, [lockedOfferId]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header variant="dark" />
        <main className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-3 border-slate-300 border-t-slate-800 rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Header variant="dark" />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-xl text-slate-600 mb-4">{error}</p>
            <Button onClick={() => navigate("/")} className="btn-primary" data-testid="go-home-btn">
              Get Your Own Offer
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const vi = data.vehicle_info;
  const vehicleName = `${vi.year} ${vi.make} ${vi.model}${vi.trim ? ` ${vi.trim}` : ""}`;
  const validDate = new Date(data.valid_until).toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
  const adjustmentColor = data.total_adjustment >= 0 ? "text-emerald-600" : "text-red-600";

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header variant="dark" />

      <main className="flex-1 py-12 sm:py-20">
        <div className="container-flow">
          {/* Shared badge */}
          <div className="text-center mb-6">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium" data-testid="shared-badge">
              <Shield className="w-4 h-4" />
              Shared Offer
            </span>
          </div>

          {/* Offer Card */}
          <div className="surface-elevated p-8 sm:p-12 text-center">
            <p className="text-slate-600 mb-1">Locked offer for</p>
            <h1 className="text-heading-2 mb-6" data-testid="shared-vehicle-name">{vehicleName}</h1>

            {/* Original */}
            <div className="mb-3">
              <p className="text-sm text-slate-500">Original Offer</p>
              <p className="text-2xl text-slate-400 line-through">{formatCurrency(data.original_offer)}</p>
            </div>

            {/* Locked */}
            <div className="py-6">
              <p className="text-label text-emerald-600 mb-2">LOCKED OFFER</p>
              <p className="offer-price text-emerald-600" data-testid="shared-offer-price">
                {formatCurrency(data.locked_offer)}
              </p>
            </div>

            {/* Adjustment */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full mb-6">
              <span className="text-sm text-slate-600">Condition Adjustment:</span>
              <span className={`font-semibold ${adjustmentColor}`}>
                {data.total_adjustment >= 0 ? "+" : ""}{formatCurrency(data.total_adjustment)}
              </span>
            </div>

            {/* Valid */}
            <div className="flex items-center justify-center gap-2 text-slate-600 mb-8">
              <Clock className="w-5 h-5" />
              <span>Valid until {validDate}</span>
            </div>

            {/* Details */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-8">
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-slate-500">VIN</p>
                <p className="font-mono font-medium text-slate-900 text-xs break-all">{vi.vin}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-slate-500">Mileage</p>
                <p className="font-medium text-slate-900">{data.mileage?.toLocaleString()} mi</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-3">
                <p className="text-slate-500">ZIP Code</p>
                <p className="font-medium text-slate-900">{data.zip_code}</p>
              </div>
            </div>

            {/* Adjustments */}
            {data.adjustments?.length > 0 && (
              <div className="text-left mt-6 border-t border-slate-200 pt-6">
                <h3 className="font-semibold text-slate-900 mb-3 text-sm">Adjustment Breakdown</h3>
                <div className="space-y-2">
                  {data.adjustments.map((adj) => (
                    <div key={adj.question} className="flex justify-between text-sm">
                      <span className="text-slate-600">{adj.question}</span>
                      <span className={`font-medium ${adj.adjustment >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                        {adj.adjustment >= 0 ? "+" : ""}{formatCurrency(adj.adjustment)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="mt-8 text-center">
            <p className="text-slate-600 mb-4">Want your own instant cash offer?</p>
            <Button onClick={() => navigate("/")} className="btn-accent h-14 px-8 text-base" data-testid="get-own-offer-btn">
              Get Your Offer
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SharedOfferScreen;
